/**
 * @fileoverview What follows seed packets being kept in the Seed vault (B137, the vault card PacketKeep): what was saved is
 * said in a toast ("Okra saved to your Seed vault." / "2 seed packets saved to your Seed vault."), and the garden is read
 * again, so the start doors turn into Today at once (a garden of kept seeds is started, the server says), the Seed vault
 * shows its seeds and the address card counts them. The photo leaving the tray is TrayPiles' afterKept (trayPhotoFiled).
 *
 * As usePacketsFiled is for plants, but no plant was made: it fires no `plant_added` tip, reads no billing (a kept seed
 * uses no Free room), and says no bed.
 *
 * Where: TrayPiles (the Seed vault card's after-step, afterKept).
 * API: none; it invalidates the plants, beds, Today brief, plant outlook, garden place (gardenWritten), the held seeds and
 * the address card's count.
 * Words (en/es): packets.keep.savedOne, packets.keep.savedMany, packets.keep.see (the toast's "See them", B163).
 *
 * @module usePacketsKept
 */

import { createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ToastAction, type ToastActionElement } from '@/shared/components/ui/toast';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { KeepResult } from '@/shared/api/packets';
import { gardenWritten } from '@/shared/lib/briefFreshness';
import { SEED_ORDERS_HELD_KEY, SEED_ORDERS_MINE_KEY } from '@/features/seed-orders/lib/seedOrderQueries';
import { refreshPlantsAndBeds } from './usePacketsFiled';

/**
 * The after-keeping steps, as one function to call with the keep route's answer.
 * The toast has a "See them" action to the vault bed (B163).
 *
 * @returns Says what was kept, and reads the garden again.
 */
export function usePacketsKept(): (result: KeepResult) => void {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (result: KeepResult): void => {
    // The vault is made on demand and counts its seeds: the beds (and plants) read again.
    refreshPlantsAndBeds(queryClient);
    // The brief and the garden place: a kept seed starts the garden, so Today's doors end (the server's word).
    gardenWritten(queryClient);
    // The vault's "N seeds kept" and the address card's count.
    void queryClient.invalidateQueries({ queryKey: SEED_ORDERS_HELD_KEY });
    void queryClient.invalidateQueries({ queryKey: SEED_ORDERS_MINE_KEY });
    const { kept } = result;
    if (kept.length === 0) return;
    toast({
      title: kept.length === 1
        ? t('packets.keep.savedOne', { name: kept[0].name })
        : t('packets.keep.savedMany', { count: kept.length }),
      // B163: the way in, past the five seconds the toast lasts; the vault bed is the keep route's.
      action: createElement(ToastAction, {
        altText: t('packets.keep.see'),
        onClick: () => navigate(`/beds/${result.vault.location_id}`),
      }, t('packets.keep.see')) as unknown as ToastActionElement, // createElement's type does not match the toast's action type
    });
  };
}
